import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import { api } from '@/lib/api'
import { downloadFile } from '@/lib/download'
import { Loader2, Film, Download, Star, Clock, AlertCircle, Plus, Lightbulb, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ArrowRightLeft, X, HelpCircle } from 'lucide-react'
import { ReferenceImageSelector } from '@/components/generation/ReferenceImageSelector'
import { AssetPickerModal } from '@/components/library/AssetPickerModal'
import { ImportIdeaModal } from '@/components/creative-lab/ImportIdeaModal'
import { MarkdownArticle } from '@/components/creative-lab/MarkdownArticle'
import { normalizeIdeaAdFormat } from '@/lib/ideaFormat'
import { filterValidMediaUrls } from '@/lib/utils'
import { embedRequirementId } from '@/lib/embedContext'
import type { ToolId, Asset, Idea, IdeaInput, GenerationSlot } from '@/types'
import { useTasksStore, type RunningTask, type TaskViewMode } from '@/stores/tasks'

type AspectRatioOption = {
  value: string
  label: string
  sublabel: string
}

type ResolutionOption = {
  value: string
  label: string
  sublabel: string
}

const STANDARD_VIDEO_ASPECT_RATIOS: AspectRatioOption[] = [
  { value: '9:16', label: '9:16', sublabel: 'Portrait/Reels' },
  { value: '16:9', label: '16:9', sublabel: 'Landscape' },
]

const SEEDANCE_ASPECT_RATIOS: AspectRatioOption[] = [
  { value: 'adaptive', label: 'Adaptive', sublabel: 'Default' },
  { value: '16:9', label: '16:9', sublabel: 'Landscape' },
  { value: '4:3', label: '4:3', sublabel: 'Classic' },
  { value: '1:1', label: '1:1', sublabel: 'Square' },
  { value: '3:4', label: '3:4', sublabel: 'Portrait' },
  { value: '9:16', label: '9:16', sublabel: 'Vertical' },
  { value: '21:9', label: '21:9', sublabel: 'Cinematic' },
]

const SORA_DURATIONS = [
  { value: 4, label: '4 seconds', credits: 3 },
  { value: 8, label: '8 seconds', credits: 6 },
  { value: 12, label: '12 seconds', credits: 9 },
]

const VEO_DURATIONS = [
  { value: 4, label: '4 seconds', credits: 5 },
  { value: 6, label: '6 seconds', credits: 8 },
  { value: 8, label: '8 seconds', credits: 10 },
]

const SEEDANCE_DURATION_CREDITS: Record<number, number> = {
  4: 2,
  5: 3,
  6: 3,
  7: 4,
  8: 4,
  9: 5,
  10: 5,
  11: 6,
  12: 6,
  13: 7,
  14: 7,
  15: 8,
}

const SEEDANCE_DURATIONS = Array.from({ length: 12 }, (_, index) => {
  const value = index + 4
  return {
    value,
    label: `${value} seconds`,
    credits: SEEDANCE_DURATION_CREDITS[value],
  }
})

const HAPPYHORSE_DURATION_CREDITS: Record<number, number> = {
  3: 2,
  4: 2,
  5: 3,
  6: 3,
  7: 4,
  8: 4,
  9: 5,
  10: 5,
  11: 6,
  12: 6,
  13: 7,
  14: 7,
  15: 8,
}

const HAPPYHORSE_DURATIONS = Array.from({ length: 13 }, (_, index) => {
  const value = index + 3
  return {
    value,
    label: `${value} seconds`,
    credits: HAPPYHORSE_DURATION_CREDITS[value],
  }
})

const HAPPYHORSE_RESOLUTIONS: ResolutionOption[] = [
  { value: '720P', label: '720P', sublabel: 'Standard' },
  { value: '1080P', label: '1080P', sublabel: 'High clarity' },
]

const MODELS: { value: ToolId; label: string; description: string }[] = [
  { value: 'seedance', label: 'Seedance', description: '' },
  { value: 'happyhorse', label: 'HappyHorse 1.0', description: '' },
  { value: 'veo', label: 'Veo 3.1', description: '' },
]
const MAX_PRODUCT_REFERENCE_IMAGES = 5
const MAX_GENERATION_COUNT = 10

function getDefaultVideoAspectRatio(model: ToolId): string {
  return model === 'seedance' ? 'adaptive' : '9:16'
}

function getVideoFrameStyle(aspectRatio: string): { aspectRatio: string; maxWidth: string; maxHeight: string } {
  switch (aspectRatio) {
    case '9:16':
      return { aspectRatio: '9 / 16', maxWidth: '360px', maxHeight: '640px' }
    case '3:4':
      return { aspectRatio: '3 / 4', maxWidth: '480px', maxHeight: '640px' }
    case '1:1':
      return { aspectRatio: '1 / 1', maxWidth: '520px', maxHeight: '520px' }
    case '4:3':
      return { aspectRatio: '4 / 3', maxWidth: '640px', maxHeight: '480px' }
    case '21:9':
      return { aspectRatio: '21 / 9', maxWidth: '720px', maxHeight: '309px' }
    case '16:9':
    case 'adaptive':
    default:
      return { aspectRatio: '16 / 9', maxWidth: '720px', maxHeight: '405px' }
  }
}

type SlotItem = { idea: Idea; ideaInput: IdeaInput } | null

function createIdeaSlot(idea: Idea): NonNullable<SlotItem> {
  return {
    idea,
    ideaInput: { idea_id: idea.id, creative_reference_url: null },
  }
}

function assignImportedIdeasToSlots(
  slots: SlotItem[],
  ideas: Idea[],
  targetIndex: number | null
): SlotItem[] {
  if (ideas.length === 0) return slots

  const next = [...slots]
  const candidateIndexes =
    targetIndex !== null
      ? [
          targetIndex,
          ...next
            .map((slot, index) => ({ slot, index }))
            .filter(({ slot, index }) => index !== targetIndex && slot === null)
            .map(({ index }) => index),
        ]
      : next
          .map((slot, index) => ({ slot, index }))
          .filter(({ slot }) => slot === null)
          .map(({ index }) => index)

  let ideaIndex = 0
  for (const slotIndex of candidateIndexes) {
    if (ideaIndex >= ideas.length) break
    next[slotIndex] = createIdeaSlot(ideas[ideaIndex])
    ideaIndex += 1
  }

  return next
}

function parsePrefilledCount(value: string | null): number {
  const parsed = Number.parseInt(value || '', 10)
  if (!Number.isFinite(parsed)) {
    return 1
  }
  return Math.max(1, Math.min(MAX_GENERATION_COUNT, parsed))
}

function getTaskBatchId(task: RunningTask): string {
  return typeof task.metadata?.batchId === 'string' ? task.metadata.batchId : task.id
}

function getTaskSlotIndex(task: RunningTask): number {
  return typeof task.metadata?.slotIndex === 'number' ? task.metadata.slotIndex : 0
}

function getTaskSlotLabel(task: RunningTask): string | null {
  const resultIdeaTitle = task.result?.[0]?.idea_title
  if (typeof resultIdeaTitle === 'string' && resultIdeaTitle.trim()) {
    return stripEstimatedDuration(resultIdeaTitle)
  }

  const metadataIdeaTitle = task.metadata?.ideaTitle
  if (typeof metadataIdeaTitle === 'string' && metadataIdeaTitle.trim()) {
    return stripEstimatedDuration(metadataIdeaTitle)
  }

  if (task.metadata?.slotType === 'freestyle') {
    return 'Freestyle'
  }

  return null
}

function stripEstimatedDuration(value: string): string {
  return value
    .trim()
    .replace(/\s*[\(\uff08]\d+(?:\.\d+)?\s*s[\)\uff09]\s*$/i, '')
    .trim()
}

function getTaskStatusLabel(task: RunningTask): string {
  if (task.status === 'completed') return 'Ready'
  if (task.status === 'failed') return 'Failed'
  if (task.status === 'processing') return 'Generating'
  return 'Queued'
}

function getTaskStatusClasses(task: RunningTask, isActive: boolean): string {
  if (task.status === 'completed') {
    return isActive ? 'border-primary bg-primary/5 text-primary' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
  }
  if (task.status === 'failed') {
    return isActive ? 'border-red-300 bg-red-50 text-red-700' : 'border-red-200 bg-red-50/70 text-red-600'
  }
  if (task.status === 'processing') {
    return isActive ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-amber-200 bg-amber-50/70 text-amber-600'
  }
  return isActive ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 bg-gray-50 text-gray-500'
}

function getNextTaskId(tasks: RunningTask[], currentTaskId: string | null, direction: 'prev' | 'next'): string | null {
  if (tasks.length === 0) return null
  const currentIndex = tasks.findIndex(task => task.id === currentTaskId)
  if (currentIndex === -1) {
    return tasks[0].id
  }

  const nextIndex = direction === 'prev'
    ? Math.max(0, currentIndex - 1)
    : Math.min(tasks.length - 1, currentIndex + 1)
  return tasks[nextIndex]?.id || null
}

type TaskBatchSummary = {
  id: string
  tasks: RunningTask[]
  totalCount: number
  latestCreatedAt: number
  completedCount: number
  failedCount: number
  processingCount: number
  hasPendingPlaceholder: boolean
}

function buildTaskBatchSummaries(tasks: RunningTask[]): TaskBatchSummary[] {
  const batches = new Map<string, RunningTask[]>()

  tasks.forEach((task) => {
    const batchId = getTaskBatchId(task)
    const existing = batches.get(batchId)
    if (existing) {
      existing.push(task)
    } else {
      batches.set(batchId, [task])
    }
  })

  return Array.from(batches.entries())
    .map(([batchId, batchTasks]) => {
      const sortedTasks = [...batchTasks].sort((a, b) => {
        const slotDelta = getTaskSlotIndex(a) - getTaskSlotIndex(b)
        if (slotDelta !== 0) return slotDelta
        return a.createdAt - b.createdAt
      })
      const declaredBatchSize = sortedTasks.reduce((max, task) => {
        return typeof task.metadata?.batchSize === 'number'
          ? Math.max(max, task.metadata.batchSize)
          : max
      }, 0)

      return {
        id: batchId,
        tasks: sortedTasks,
        totalCount: Math.max(sortedTasks.length, declaredBatchSize || sortedTasks.length),
        latestCreatedAt: Math.max(...sortedTasks.map((task) => task.createdAt)),
        completedCount: sortedTasks.filter((task) => task.status === 'completed' && task.result?.[0]?.url).length,
        failedCount: sortedTasks.filter((task) => task.status === 'failed').length,
        processingCount: sortedTasks.filter((task) => task.status === 'pending' || task.status === 'processing').length,
        hasPendingPlaceholder: sortedTasks.some((task) => task.id.startsWith('pending-')),
      }
    })
    .sort((a, b) => b.latestCreatedAt - a.latestCreatedAt)
}

function getErrorMessage(error: unknown, fallback: string): string {
  const responseData = (error as { response?: { data?: { error?: { message?: string }; detail?: string } } })?.response?.data
  return responseData?.error?.message || responseData?.detail || (error instanceof Error ? error.message : fallback)
}

export function VideoFreestylePage() {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const preselectedProductId = searchParams.get('product') || searchParams.get('product_id') || undefined
  const preselectedVerticalSkillKey =
    searchParams.get('vertical_skill') || searchParams.get('vertical_skill_key') || undefined
  const preselectedIdeaSource = searchParams.get('idea_source') || undefined
  const preselectedIdeaTag = searchParams.get('idea_tag') || undefined
  const preselectedCount = parsePrefilledCount(searchParams.get('count'))
  const [prompt, setPrompt] = useState('')
  const [aspectRatio, setAspectRatio] = useState(getDefaultVideoAspectRatio('veo'))
  const [model, setModel] = useState<ToolId>('veo')
  const [duration, setDuration] = useState(8)
  const [resolution, setResolution] = useState('720P')
  const [count, setCount] = useState(preselectedCount)
  const [productId, setProductId] = useState<string | undefined>(preselectedProductId)
  const [verticalSkillKey, setVerticalSkillKey] = useState(preselectedVerticalSkillKey || 'none')
  const [isVerticalGuidanceOpen, setIsVerticalGuidanceOpen] = useState(false)

  // Product reference images state (shared by both modes)
  const [manualReferenceAssets, setManualReferenceAssets] = useState<Asset[]>([])
  const [includeProductScreenshot, setIncludeProductScreenshot] = useState(true)
  const [showAssetPicker, setShowAssetPicker] = useState(false)

  // Unified mode: Use Ideas toggle (default ON)
  const [useIdeas, setUseIdeas] = useState(true)

  // Slot-based state
  const [slots, setSlots] = useState<SlotItem[]>([null])

  // Slot carousel: which slot is currently visible
  const [activeSlotIndex, setActiveSlotIndex] = useState(0)

  // Modal state
  const [showImportModal, setShowImportModal] = useState(false)
  const [replacingSlotIndex, setReplacingSlotIndex] = useState<number | null>(null)

  // Use global task store
  const { tasks, addTask, updateTask, setTasksViewMode, startPolling, removeTask, isPolling } = useTasksStore()
  const [focusedBatchId, setFocusedBatchId] = useState<string | null>(null)

  const videoTasks = useMemo(
    () => Object.values(tasks)
      .filter(task => task.type === 'video_freestyle')
      .sort((a, b) => b.createdAt - a.createdAt),
    [tasks]
  )
  const foregroundVideoTasks = useMemo(
    () => videoTasks.filter((task) => task.viewMode !== 'background'),
    [videoTasks]
  )
  const backgroundVideoTasks = useMemo(
    () => videoTasks.filter((task) => task.viewMode === 'background'),
    [videoTasks]
  )
  const foregroundBatches = useMemo(
    () => buildTaskBatchSummaries(foregroundVideoTasks),
    [foregroundVideoTasks]
  )
  const backgroundBatches = useMemo(
    () => buildTaskBatchSummaries(backgroundVideoTasks),
    [backgroundVideoTasks]
  )
  const currentBatchId = useMemo(() => {
    if (focusedBatchId && foregroundBatches.some((batch) => batch.id === focusedBatchId)) {
      return focusedBatchId
    }
    return foregroundBatches[0]?.id ?? null
  }, [focusedBatchId, foregroundBatches])
  const currentBatchSummary = useMemo(
    () => foregroundBatches.find((batch) => batch.id === currentBatchId) ?? null,
    [currentBatchId, foregroundBatches]
  )
  const currentBatchTasks = currentBatchSummary?.tasks ?? []
  const [activeBatchTaskId, setActiveBatchTaskId] = useState<string | null>(null)
  const initializedSkillProductIdRef = useRef<string | null>(null)

  // Reset scroll position when component mounts
  useEffect(() => {
    const main = document.querySelector('main')
    if (main) main.scrollTop = 0
  }, [])

  useEffect(() => {
    if (focusedBatchId && !foregroundBatches.some((batch) => batch.id === focusedBatchId)) {
      setFocusedBatchId(null)
    }
  }, [focusedBatchId, foregroundBatches])

  // Resume polling for processing tasks
  useEffect(() => {
    videoTasks.forEach((task) => {
      if (
        (task.status === 'processing' || task.status === 'pending') &&
        !task.id.startsWith('pending-') &&
        !isPolling(task.id)
      ) {
        startPolling(task.id)
      }
    })
  }, [videoTasks, startPolling, isPolling])

  // Refresh subscription when task completes
  useEffect(() => {
    if (videoTasks.some(task => task.status === 'completed' || task.status === 'failed')) {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    }
  }, [videoTasks, queryClient])

  // Notify hui parent frame when videos are ready (once per task)
  const notifiedVideoTaskIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (window.self === window.top) return
    const newlyCompleted = videoTasks.filter(
      (task) => task.status === 'completed' && task.result?.[0]?.url && !notifiedVideoTaskIdsRef.current.has(task.id),
    )
    if (newlyCompleted.length === 0) return
    newlyCompleted.forEach((task) => notifiedVideoTaskIdsRef.current.add(task.id))
    window.parent.postMessage({
      type: 'ASSETS_GENERATED',
      assets: newlyCompleted.map((task) => ({
        url: task.result![0].url,
        media_type: 'video',
        first_frame_url: task.result![0].first_frame_url || null,
      })),
      requirement_id: embedRequirementId,
    }, '*')
  }, [videoTasks])

  useEffect(() => {
    if (currentBatchTasks.length === 0) {
      setActiveBatchTaskId(null)
      return
    }

    const activeTask = currentBatchTasks.find(task => task.id === activeBatchTaskId)
    const firstCompletedTask = currentBatchTasks.find(task => task.status === 'completed' && task.result?.[0]?.url)

    if (!activeTask || (!activeTask.result?.[0]?.url && firstCompletedTask)) {
      setActiveBatchTaskId((firstCompletedTask || currentBatchTasks[0]).id)
    }
  }, [activeBatchTaskId, currentBatchTasks])

  const activeBatchTask = currentBatchTasks.find(task => task.id === activeBatchTaskId) || currentBatchTasks[0] || null
  const activeBatchTaskIndex = activeBatchTask
    ? currentBatchTasks.findIndex(task => task.id === activeBatchTask.id)
    : -1
  const activeVideoNumber = activeBatchTaskIndex >= 0 ? activeBatchTaskIndex + 1 : 1
  const totalTaskCount = currentBatchSummary?.totalCount ?? currentBatchTasks.length
  const completedTaskCount = currentBatchSummary?.completedCount ?? 0
  const failedTaskCount = currentBatchSummary?.failedCount ?? 0
  const processingTaskCount = currentBatchSummary?.processingCount ?? 0
  const foregroundProcessingTaskCount = foregroundVideoTasks.filter(
    (task) => task.status === 'pending' || task.status === 'processing'
  ).length
  const isTaskRunning = foregroundProcessingTaskCount > 0
  const taskError = activeBatchTask?.error || currentBatchTasks.find(task => task.status === 'failed')?.error || null
  const activeAspectRatio = typeof activeBatchTask?.metadata?.aspectRatio === 'string'
    ? activeBatchTask.metadata.aspectRatio
    : aspectRatio
  const activeVideoFrameStyle = getVideoFrameStyle(activeAspectRatio)
  const generatedVideo = activeBatchTask?.result?.[0] ? {
    id: activeBatchTask.result[0].id,
    url: activeBatchTask.result[0].url || '',
    thumbnail: activeBatchTask.result[0].first_frame_url || activeBatchTask.result[0].url || '',
    duration: activeBatchTask.metadata?.duration || duration,
    ideaTitle: activeBatchTask.result[0].idea_title || getTaskSlotLabel(activeBatchTask),
    firstFrameUrl: activeBatchTask.result[0].first_frame_url || null,
  } : null

  const handleGeneratedVideoDownload = async () => {
    if (!generatedVideo?.id) return

    try {
      const result = await api.recordAssetDownload(generatedVideo.id)
      if (!result.download_url) {
        toast({ title: 'Download unavailable', variant: 'destructive' })
        return
      }
      downloadFile(result.download_url)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  // Get duration options based on selected model
  const durations = useMemo(() => {
    if (model === 'sora') return SORA_DURATIONS
    if (model === 'veo') return VEO_DURATIONS
    if (model === 'seedance') return SEEDANCE_DURATIONS
    if (model === 'happyhorse') return HAPPYHORSE_DURATIONS
    return SORA_DURATIONS
  }, [model])

  const selectedDurationOption = useMemo(
    () => durations.find((option) => option.value === duration) || durations[0],
    [durations, duration]
  )
  const usesDurationSlider = model === 'seedance' || model === 'happyhorse'
  const durationSliderMin = durations[0]?.value ?? duration
  const durationSliderMax = durations[durations.length - 1]?.value ?? duration
  const durationPricingLabel = model === 'happyhorse' ? 'HappyHorse pricing' : 'Seedance pricing'

  const aspectRatioOptions = useMemo(
    () => (model === 'seedance' ? SEEDANCE_ASPECT_RATIOS : STANDARD_VIDEO_ASPECT_RATIOS),
    [model]
  )
  const resolutionOptions = useMemo(
    () => (model === 'happyhorse' ? HAPPYHORSE_RESOLUTIONS : []),
    [model]
  )

  // Reset duration when model changes if invalid
  useEffect(() => {
    let newDurations
    if (model === 'sora') newDurations = SORA_DURATIONS
    else if (model === 'veo') newDurations = VEO_DURATIONS
    else if (model === 'seedance') newDurations = SEEDANCE_DURATIONS
    else if (model === 'happyhorse') newDurations = HAPPYHORSE_DURATIONS
    else newDurations = SORA_DURATIONS
    const validValues = newDurations.map(d => d.value)
    if (!validValues.includes(duration)) {
      setDuration(newDurations[0].value)
    }
  }, [model, duration])

  const previousModelRef = useRef<ToolId>(model)

  useEffect(() => {
    const previousModel = previousModelRef.current
    const validRatios = aspectRatioOptions.map((option) => option.value)

    if (model === 'seedance' && previousModel !== 'seedance') {
      setAspectRatio(getDefaultVideoAspectRatio(model))
    } else if (previousModel === 'seedance' && model !== 'seedance' && aspectRatio === 'adaptive') {
      setAspectRatio(getDefaultVideoAspectRatio(model))
    } else if (!validRatios.includes(aspectRatio)) {
      setAspectRatio(getDefaultVideoAspectRatio(model))
    }

    previousModelRef.current = model
  }, [model, aspectRatio, aspectRatioOptions])

  useEffect(() => {
    if (model !== 'happyhorse') {
      setResolution('720P')
      return
    }
    const validResolutions = HAPPYHORSE_RESOLUTIONS.map((option) => option.value)
    if (!validResolutions.includes(resolution)) {
      setResolution(HAPPYHORSE_RESOLUTIONS[0].value)
    }
  }, [model, resolution])

  // Get products
  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(0, 100),
  })

  const { data: preselectedProduct } = useQuery({
    queryKey: ['product', preselectedProductId, 'video-freestyle-prefill'],
    queryFn: () => api.getProduct(preselectedProductId!),
    enabled: !!preselectedProductId,
  })

  const availableProducts = useMemo(() => {
    const items = products?.items || []
    if (!preselectedProduct) {
      return items
    }
    return items.some((item) => item.id === preselectedProduct.id)
      ? items
      : [preselectedProduct, ...items]
  }, [products?.items, preselectedProduct])

  const selectedProduct = useMemo(
    () => availableProducts.find((item) => item.id === productId) || null,
    [availableProducts, productId]
  )
  const shouldShowVerticalSkill = !!selectedProduct
  const productScreenshotReference = useMemo<Asset | null>(() => {
    if (!selectedProduct?.screenshot_url) {
      return null
    }

    return {
      id: `product-screenshot-${selectedProduct.id}`,
      asset_type: 'image',
      source_type: 'screenshot',
      url: selectedProduct.screenshot_url,
      download_url: null,
      file_name: 'Product Screenshot',
      description: null,
      score: null,
      is_favorited: false,
      created_at: selectedProduct.created_at,
      product_id: selectedProduct.id,
      parent_asset_id: null,
    }
  }, [selectedProduct])
  const isProductScreenshotIncluded = Boolean(productScreenshotReference) && includeProductScreenshot
  const maxManualReferenceCount = isProductScreenshotIncluded
    ? Math.max(0, MAX_PRODUCT_REFERENCE_IMAGES - 1)
    : MAX_PRODUCT_REFERENCE_IMAGES
  const referenceHelperText = isProductScreenshotIncluded
    ? `The product screenshot is included automatically. You can add up to ${maxManualReferenceCount} more reference images for first-frame generation.`
    : selectedProduct?.screenshot_url
      ? `The product screenshot has been removed. Add up to ${MAX_PRODUCT_REFERENCE_IMAGES} product reference images for first-frame generation.`
    : `Add up to ${MAX_PRODUCT_REFERENCE_IMAGES} product reference images for first-frame generation.`
  const referenceAssets = useMemo(() => {
    const assets: Asset[] = []
    const seenIds = new Set<string>()

    if (productScreenshotReference && includeProductScreenshot) {
      assets.push(productScreenshotReference)
      seenIds.add(productScreenshotReference.id)
    }

    for (const asset of manualReferenceAssets) {
      if (!seenIds.has(asset.id)) {
        assets.push(asset)
        seenIds.add(asset.id)
      }
    }

    return assets
  }, [includeProductScreenshot, manualReferenceAssets, productScreenshotReference])

  useEffect(() => {
    setIncludeProductScreenshot(true)
  }, [selectedProduct?.id])

  useEffect(() => {
    if (preselectedProductId) {
      setProductId(preselectedProductId)
    }
  }, [preselectedProductId])

  useEffect(() => {
    initializedSkillProductIdRef.current = null
    if (!productId) {
      setVerticalSkillKey('none')
      return
    }
    if (productId === preselectedProductId && preselectedVerticalSkillKey) {
      setVerticalSkillKey(preselectedVerticalSkillKey)
      return
    }
    setVerticalSkillKey('none')
  }, [productId, preselectedProductId, preselectedVerticalSkillKey])

  const { data: verticalSkillOptions, isLoading: isVerticalSkillOptionsLoading } = useQuery({
    queryKey: ['vertical-skill-options', productId, 'video'],
    queryFn: () => api.getVerticalSkillOptions(productId!, 'video'),
    enabled: !!productId,
  })

  useEffect(() => {
    if (!productId || !shouldShowVerticalSkill) {
      setVerticalSkillKey('none')
      initializedSkillProductIdRef.current = null
      return
    }
    if (initializedSkillProductIdRef.current === productId) {
      return
    }
    if (verticalSkillOptions) {
      const preselectedOption = productId === preselectedProductId ? preselectedVerticalSkillKey : undefined
      const resolvedKey =
        preselectedOption && verticalSkillOptions.options.some((option) => option.key === preselectedOption)
          ? preselectedOption
          : verticalSkillOptions.default_skill_key || 'none'
      setVerticalSkillKey(resolvedKey)
      initializedSkillProductIdRef.current = productId
    }
  }, [productId, preselectedProductId, preselectedVerticalSkillKey, shouldShowVerticalSkill, verticalSkillOptions])

  useEffect(() => {
    setIsVerticalGuidanceOpen(false)
  }, [productId, verticalSkillKey, shouldShowVerticalSkill])

  const selectedVerticalSkillOption = verticalSkillOptions?.options.find((option) => option.key === verticalSkillKey) || null
  const activeIdeaSource = productId && productId === preselectedProductId ? preselectedIdeaSource : undefined
  const activeIdeaTag = productId && productId === preselectedProductId ? preselectedIdeaTag : undefined
  const defaultIdeaPoolLabel = useMemo(() => {
    if (!activeIdeaSource) return ''
    return activeIdeaTag ? `${activeIdeaSource} / ${activeIdeaTag}` : activeIdeaSource
  }, [activeIdeaSource, activeIdeaTag])

  useEffect(() => {
    if (manualReferenceAssets.length > maxManualReferenceCount) {
      setManualReferenceAssets((prev) => prev.slice(0, maxManualReferenceCount))
    }
  }, [manualReferenceAssets.length, maxManualReferenceCount])

  const previousReferenceProductIdRef = useRef<string | null>(null)
  useEffect(() => {
    const nextProductId = selectedProduct?.id ?? null
    if (previousReferenceProductIdRef.current === nextProductId) {
      return
    }
    previousReferenceProductIdRef.current = nextProductId
    setManualReferenceAssets([])
  }, [selectedProduct?.id])

  // Get credits
  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.getSubscription(),
  })

  // Fetch ideas for auto-recommendation
  const { data: ideasData } = useQuery({
    queryKey: ['ideas', productId, 'unused_first', activeIdeaSource, activeIdeaTag],
    queryFn: () =>
      api.getIdeas(productId!, {
        sort: 'unused_first',
        limit: 100,
        source: activeIdeaSource,
        tag: activeIdeaTag,
      }),
    enabled: !!productId && useIdeas,
  })

  const availableIdeas = useMemo(
    () => (ideasData?.items || []).filter((idea) => normalizeIdeaAdFormat(idea.ad_format) === 'video'),
    [ideasData?.items]
  )

  // Auto-fill slots with recommended ideas
  const autoFillSlots = useCallback((newCount: number, currentSlots: SlotItem[], ideas: Idea[]) => {
    if (!useIdeas) return Array(newCount).fill(null)

    const newSlots: SlotItem[] = []
    const usedIdeaIds = new Set<string>()

    for (let i = 0; i < Math.min(newCount, currentSlots.length); i++) {
      const slot = currentSlots[i]
      if (slot) {
        newSlots.push(slot)
        usedIdeaIds.add(slot.idea.id)
      } else {
        newSlots.push(null)
      }
    }

    while (newSlots.length < newCount) {
      newSlots.push(null)
    }

    const unusedIdeas = ideas.filter(idea => !usedIdeaIds.has(idea.id))
    let ideaIdx = 0
    for (let i = 0; i < newSlots.length; i++) {
      if (!newSlots[i] && ideaIdx < unusedIdeas.length) {
        const idea = unusedIdeas[ideaIdx]
        newSlots[i] = {
          idea,
          ideaInput: { idea_id: idea.id, creative_reference_url: null },
        }
        usedIdeaIds.add(idea.id)
        ideaIdx++
      }
    }

    return newSlots
  }, [useIdeas])

  // Re-fill slots when count, useIdeas, or available ideas change
  useEffect(() => {
    setSlots(prev => autoFillSlots(count, prev, availableIdeas))
  }, [count, useIdeas, availableIdeas, autoFillSlots])

  // Count spinner handlers
  const handleCountChange = (newCount: number) => {
    const clamped = Math.max(1, Math.min(MAX_GENERATION_COUNT, newCount))
    setCount(clamped)
    setActiveSlotIndex(prev => Math.min(prev, clamped - 1))
  }

  const appendManualReferenceAssets = useCallback((incomingAssets: Asset[]) => {
    let shouldIncludeCurrentProductScreenshot = false
    const selectedProductId = selectedProduct?.id

    setManualReferenceAssets((prev) => {
      const next = [...prev]
      const seenIds = new Set(prev.map((asset) => asset.id))

      for (const asset of incomingAssets) {
        const isCurrentProductScreenshot =
          Boolean(selectedProductId) &&
          asset.source_type === 'screenshot' &&
          asset.product_id === selectedProductId

        if (isCurrentProductScreenshot) {
          shouldIncludeCurrentProductScreenshot = true
          continue
        }

        if (seenIds.has(asset.id) || next.length >= maxManualReferenceCount) {
          continue
        }

        next.push(asset)
        seenIds.add(asset.id)
      }

      return next
    })

    if (shouldIncludeCurrentProductScreenshot) {
      setIncludeProductScreenshot(true)
    }
  }, [maxManualReferenceCount, selectedProduct?.id])

  const resolvedReferenceAssetIds = manualReferenceAssets.map((asset) => asset.id)
  const resolvedReferenceAssetId = resolvedReferenceAssetIds[0]

  const startVideoGeneration = useCallback(async (viewMode: TaskViewMode) => {
    if (
      viewMode === 'foreground' &&
      currentBatchTasks.length > 0 &&
      currentBatchTasks.every(task => task.status === 'completed' || task.status === 'failed')
    ) {
      currentBatchTasks.forEach(task => removeTask(task.id))
    }

    const apiSlots: GenerationSlot[] = slots.map((slot) =>
      slot ? { idea_id: slot.idea.id, creative_reference_url: slot.ideaInput.creative_reference_url || null } : null
    )
    const requestKey = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const pendingId = `pending-${requestKey}`
    const batchId = `video-batch-${requestKey}`
    const sharedMetadata = {
      batchId,
      batchSize: count,
      count,
      duration,
      aspectRatio,
      resolution: model === 'happyhorse' ? resolution : undefined,
      model,
      productId,
      useIdeas,
    }

    addTask({
      id: pendingId,
      type: 'video_freestyle',
      status: 'pending',
      viewMode,
      metadata: sharedMetadata,
    })

    if (viewMode === 'foreground') {
      setFocusedBatchId(batchId)
      setActiveBatchTaskId(pendingId)
    }

    try {
      const data = await api.generateVideoWithIdeas({
        slots: apiSlots,
        prompt: prompt || undefined,
        model,
        duration_seconds: duration,
        aspect_ratio: aspectRatio,
        resolution: model === 'happyhorse' ? resolution : undefined,
        product_id: productId,
        include_product_screenshot: includeProductScreenshot,
        reference_asset_id: resolvedReferenceAssetId,
        reference_asset_ids: resolvedReferenceAssetIds,
        vertical_skill_key: productId ? verticalSkillKey : undefined,
        requirement_id: embedRequirementId ?? undefined,
      })

      const resolvedViewMode = useTasksStore.getState().getTask(pendingId)?.viewMode ?? viewMode
      removeTask(pendingId)

      data.tasks.forEach((task, index) => {
        const slot = slots[index]
        addTask({
          id: task.id,
          type: 'video_freestyle',
          status: 'processing',
          viewMode: resolvedViewMode,
          metadata: {
            ...sharedMetadata,
            batchSize: data.tasks.length,
            slotIndex: index,
            slotType: slot ? 'idea' : 'freestyle',
            ideaTitle: slot?.idea.title || null,
          },
        })
        startPolling(task.id)
      })

      if (resolvedViewMode === 'foreground') {
        setFocusedBatchId(batchId)
        setActiveBatchTaskId(data.tasks[0]?.id ?? null)
      } else {
        toast({
          title: data.tasks.length > 1 ? 'Video batch started in background' : 'Video task started in background',
          description: 'You can keep generating while it runs.',
        })
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to start generation')
      updateTask(pendingId, {
        status: 'failed',
        error: message,
      })

      if (viewMode === 'background') {
        toast({
          title: 'Background video start failed',
          description: message,
          variant: 'destructive',
        })
      }
    }
  }, [
    addTask,
    aspectRatio,
    count,
    currentBatchTasks,
    duration,
    includeProductScreenshot,
    model,
    productId,
    prompt,
    removeTask,
    resolvedReferenceAssetId,
    resolvedReferenceAssetIds,
    resolution,
    slots,
    startPolling,
    updateTask,
    useIdeas,
    verticalSkillKey,
  ])

  const handleGenerate = () => {
    void startVideoGeneration('foreground')
  }

  const handleGenerateInBackground = () => {
    void startVideoGeneration('background')
  }

  const handleSendCurrentBatchToBackground = () => {
    if (currentBatchTasks.length === 0) {
      return
    }

    setTasksViewMode(currentBatchTasks.map((task) => task.id), 'background')
    setFocusedBatchId(null)
    setActiveBatchTaskId(null)
    toast({
      title: 'Video batch moved to background',
      description: 'Progress will continue in the task tray and Tasks page.',
    })
  }

  const handleBringBatchToFront = (batchId: string) => {
    const batch = backgroundBatches.find((item) => item.id === batchId)
    if (!batch) {
      return
    }

    setTasksViewMode(batch.tasks.map((task) => task.id), 'foreground')
    setFocusedBatchId(batchId)
    setActiveBatchTaskId(batch.tasks[0]?.id ?? null)
  }

  // Slot operations
  const handleRemoveIdea = (index: number) => {
    setSlots(prev => {
      const next = [...prev]
      const usedIds = new Set(next.filter(s => s !== null).map(s => s!.idea.id))
      if (next[index]) usedIds.delete(next[index]!.idea.id)
      const replacement = availableIdeas.find(idea => !usedIds.has(idea.id))
      next[index] = replacement
        ? createIdeaSlot(replacement)
        : null
      return next
    })
  }

  const handleReplaceIdea = (index: number) => {
    setReplacingSlotIndex(index)
    setShowImportModal(true)
  }

  const handleAddIdeaToSlot = (index: number) => {
    setReplacingSlotIndex(index)
    setShowImportModal(true)
  }

  const handleCreativeRefChange = (index: number, url: string | null) => {
    setSlots(prev =>
      prev.map((slot, i) =>
        i === index && slot
          ? { ...slot, ideaInput: { ...slot.ideaInput, creative_reference_url: url } }
          : slot
      )
    )
  }

  const ideaSlotCount = slots.filter(s => s !== null).length
  const freestyleSlotCount = slots.filter(s => s === null).length

  const selectedDuration = durations.find((d) => d.value === duration)
  const creditsPerVideo = selectedDuration?.credits || 10
  const creditsNeeded = creditsPerVideo * count
  const hasEnoughCredits = (subscription?.credits_available || 0) >= creditsNeeded

  const existingIdeaIds = slots.filter(s => s !== null).map(s => s!.idea.id)
  const replaceableIdeaId =
    replacingSlotIndex !== null && slots[replacingSlotIndex]
      ? slots[replacingSlotIndex]!.idea.id
      : null
  const importExcludedIdeaIds = replaceableIdeaId
    ? existingIdeaIds.filter((ideaId) => ideaId !== replaceableIdeaId)
    : existingIdeaIds
  const maxImportSelection = useMemo(() => {
    const occupiedIdeaSlots = slots.filter((slot) => slot !== null).length
    const availableSlotCount =
      count -
      (occupiedIdeaSlots - (replacingSlotIndex !== null && slots[replacingSlotIndex] ? 1 : 0))
    return Math.max(1, availableSlotCount)
  }, [count, replacingSlotIndex, slots])

  return (
    <div className="space-y-6 min-h-0">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Generation Form */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
            {/* Product Association (shared) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Associate with Product <span className="text-gray-400">(Optional)</span>
              </label>
              <select
                value={productId || ''}
                onChange={(e) => setProductId(e.target.value || undefined)}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                disabled={isTaskRunning}
              >
                <option value="">No product</option>
                {availableProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.product_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Product Reference Images (shared) */}
            <ReferenceImageSelector
              selectedAssets={referenceAssets}
              onSelect={() => setShowAssetPicker(true)}
              onClearAsset={(asset) => {
                if (asset.id.startsWith('product-screenshot-')) {
                  setIncludeProductScreenshot(false)
                  return
                }
                setManualReferenceAssets((prev) => prev.filter((item) => item.id !== asset.id))
              }}
              canClearAsset={() => true}
              addButtonLabel="Add reference"
              canAddMore={manualReferenceAssets.length < maxManualReferenceCount}
              disabled={isTaskRunning}
              emptyTitle="Add product references"
              emptyDescription={referenceHelperText}
              helperText={referenceHelperText}
              selectedHelperText="Selected references are used to generate the first frame. The final video is then generated from that first frame."
              showLabel={false}
              showSourceLabel={false}
              multiSelectLayout="horizontal"
            />

            {/* Count Spinner + Use Ideas Toggle */}
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Videos
                </label>
                <div className="inline-flex items-stretch">
                  <button
                    type="button"
                    onClick={() => handleCountChange(count - 1)}
                    disabled={isTaskRunning || count <= 1}
                    className="flex items-center justify-center w-9 h-9 border border-gray-300 rounded-l-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={count}
                    onChange={(e) => handleCountChange(parseInt(e.target.value) || 1)}
                    disabled={isTaskRunning}
                    className="w-12 h-9 text-center text-sm border-t border-b border-gray-300 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => handleCountChange(count + 1)}
                    disabled={isTaskRunning || count >= 10}
                    className="flex items-center justify-center w-9 h-9 border border-gray-300 rounded-r-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex min-w-[96px] flex-col items-center justify-end pl-2">
                <label className="mb-1.5 flex items-center justify-center gap-1 text-center text-sm font-medium text-gray-700">
                  Use Ideas
                  <span className="relative group">
                    <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block w-56 px-2.5 py-1.5 text-xs text-white bg-gray-800 rounded-md shadow-lg z-50 font-normal leading-relaxed">
                      When enabled, slots are auto-filled with recommended Ideas from your product's idea library. Each Idea provides creative direction and optional style references.
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={useIdeas}
                  onClick={() => setUseIdeas(!useIdeas)}
                  disabled={isTaskRunning}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                    useIdeas ? 'bg-primary' : 'bg-gray-200'
                  } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                      useIdeas ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {useIdeas && defaultIdeaPoolLabel && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                Default idea pool: {defaultIdeaPoolLabel}
              </div>
            )}

            {/* Slot Carousel (only when Use Ideas is ON) */}
            {useIdeas && (
              <div>
                {/* Navigation header */}
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">
                    {count} video{count !== 1 ? 's' : ''}
                    {ideaSlotCount > 0 && `: ${ideaSlotCount} with Ideas`}
                    {freestyleSlotCount > 0 && `${ideaSlotCount > 0 ? ' + ' : ': '}${freestyleSlotCount} Freestyle`}
                    {freestyleSlotCount > 0 && availableIdeas.length === 0 && productId && (
                      <span className="text-amber-600"> — Not enough ideas</span>
                    )}
                  </p>
                  {count > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setActiveSlotIndex(prev => Math.max(0, prev - 1))}
                        disabled={activeSlotIndex === 0}
                        className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-xs text-gray-500 tabular-nums w-10 text-center">
                        {activeSlotIndex + 1} / {count}
                      </span>
                      <button
                        type="button"
                        onClick={() => setActiveSlotIndex(prev => Math.min(count - 1, prev + 1))}
                        disabled={activeSlotIndex === count - 1}
                        className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Active slot card */}
                {(() => {
                  const slot = slots[activeSlotIndex]
                  const index = activeSlotIndex
                  return slot ? (
                    /* Idea Slot */
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Lightbulb className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                            <p className="font-medium text-sm truncate">{slot.idea.title}</p>
                            {slot.idea.used_count === 0 ? (
                              <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded flex-shrink-0">New</span>
                            ) : (
                              <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">Used {slot.idea.used_count}x</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{slot.idea.description}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleReplaceIdea(index)}
                            disabled={isTaskRunning}
                            className="p-1 text-gray-400 hover:text-primary hover:bg-primary/5 rounded transition-colors"
                            title="Replace idea"
                          >
                            <ArrowRightLeft className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleRemoveIdea(index)}
                            disabled={isTaskRunning}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Remove idea"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Creative Reference Selection */}
                      {filterValidMediaUrls(slot.idea.media_urls).length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 mb-1.5">Creative Ref (optional):</p>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => handleCreativeRefChange(index, null)}
                              className={`px-2 py-1 text-xs rounded border transition-colors ${
                                !slot.ideaInput.creative_reference_url
                                  ? 'border-primary bg-primary/5 text-primary'
                                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
                              }`}
                            >
                              None
                            </button>
                            {filterValidMediaUrls(slot.idea.media_urls).map((url, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleCreativeRefChange(index, url)}
                                className={`relative rounded overflow-hidden border-2 transition-colors ${
                                  slot.ideaInput.creative_reference_url === url
                                    ? 'border-primary'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <img src={url} alt={`Ref ${idx + 1}`} className="w-10 h-10 object-cover" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Freestyle Slot */
                    <div className="bg-gray-50 rounded-lg border border-dashed border-gray-300 p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <span className="text-xs">Freestyle — uses prompt only</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddIdeaToSlot(index)}
                          disabled={isTaskRunning}
                          className="text-xs text-primary hover:text-primary/80 flex items-center gap-0.5"
                        >
                          <Plus className="h-3 w-3" />
                          Add Idea
                        </button>
                      </div>
                    </div>
                  )
                })()}

                {/* Dot indicators for quick navigation */}
                {count > 1 && (
                  <div className="flex items-center justify-center gap-1 mt-2">
                    {slots.map((slot, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveSlotIndex(i)}
                        className={`rounded-full transition-colors ${
                          i === activeSlotIndex
                            ? 'w-2 h-2 bg-primary'
                            : slot
                              ? 'w-1.5 h-1.5 bg-amber-300 hover:bg-amber-400'
                              : 'w-1.5 h-1.5 bg-gray-300 hover:bg-gray-400'
                        }`}
                        title={`Slot ${i + 1}${slot ? `: ${slot.idea.title}` : ' (Freestyle)'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Prompt */}
            <div>
              {shouldShowVerticalSkill && (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vertical Guidance
                    </label>
                    <select
                      value={verticalSkillKey}
                      onChange={(e) => setVerticalSkillKey(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                      disabled={isTaskRunning || isVerticalSkillOptionsLoading}
                    >
                      {isVerticalSkillOptionsLoading && <option value="none">Loading skills...</option>}
                      {!isVerticalSkillOptionsLoading && verticalSkillOptions?.options.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                    {selectedVerticalSkillOption?.content ? (
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setIsVerticalGuidanceOpen((open) => !open)}
                          className="flex w-full items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                        >
                          <span>{isVerticalGuidanceOpen ? 'Hide Guidance' : 'View Guidance'}</span>
                          {isVerticalGuidanceOpen ? (
                            <ChevronUp className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          )}
                        </button>
                        {isVerticalGuidanceOpen && (
                          <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
                            <MarkdownArticle
                              markdown={selectedVerticalSkillOption.content}
                              className="text-sm"
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">
                        No industry guidance is applied for this run.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-2">
                {useIdeas ? (
                  <>Prompt <span className="text-gray-400">(applied to all / used by Freestyle slots)</span></>
                ) : (
                  <>User Preference <span className="text-gray-400">(Optional)</span></>
                )}
              </label>
              <textarea
                rows={3}
                placeholder={useIdeas
                  ? 'Add extra style or content guidance for all slots...'
                  : 'Describe the video style or content you prefer...'
                }
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                disabled={isTaskRunning}
              />
            </div>

            {/* Model */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
              <div className="space-y-2">
                {MODELS.map((m) => (
                  <label
                    key={m.value}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      model === m.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="model"
                      value={m.value}
                      checked={model === m.value}
                      onChange={(e) => setModel(e.target.value as ToolId)}
                      className="sr-only"
                      disabled={isTaskRunning}
                    />
                    <div>
                      <p className="font-medium">{m.label}</p>
                      {m.description && <p className="text-sm text-gray-500">{m.description}</p>}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Duration
              </label>
              {usesDurationSlider ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <div className="flex items-end justify-between gap-3">
                      <div>
                        <div className="text-2xl font-semibold text-gray-900">{selectedDurationOption.value}s</div>
                        <div className="text-xs text-gray-500">Selected duration</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-700">{selectedDurationOption.credits} credits</div>
                        <div className="text-xs text-gray-500">{durationPricingLabel}</div>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={durationSliderMin}
                      max={durationSliderMax}
                      step={1}
                      value={duration}
                      onChange={(e) => setDuration(Number(e.target.value))}
                      disabled={isTaskRunning}
                      className={`mt-4 h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-primary ${
                        isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    />
                    <div className="mt-2 flex justify-between text-xs text-gray-500">
                      <span>{durationSliderMin}s</span>
                      <span>{durationSliderMax}s</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {durations.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => setDuration(d.value)}
                      disabled={isTaskRunning}
                      className={`flex-1 px-2 py-2 text-sm border rounded-lg transition-colors ${
                        duration === d.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="font-medium">{d.value}s</div>
                      <div className="text-xs text-gray-500">{d.credits} credits</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Resolution */}
            {resolutionOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resolution</label>
                <div className="grid grid-cols-2 gap-3">
                  {resolutionOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setResolution(option.value)}
                      disabled={isTaskRunning}
                      className={`flex flex-col items-center justify-center px-3 py-3 border rounded-lg transition-colors ${
                        resolution === option.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="font-medium text-sm">{option.label}</span>
                      <span className="text-xs text-gray-500">{option.sublabel}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Aspect Ratio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
              <div className={aspectRatioOptions.length > 4 ? 'grid grid-cols-2 gap-3 sm:grid-cols-3' : 'grid grid-cols-2 gap-3'}>
                {aspectRatioOptions.map((ar) => (
                  <button
                    key={ar.value}
                    type="button"
                    onClick={() => setAspectRatio(ar.value)}
                    disabled={isTaskRunning}
                    className={`flex flex-col items-center justify-center px-3 py-3 border rounded-lg transition-colors ${
                      aspectRatio === ar.value
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className="font-medium text-sm">{ar.label}</span>
                    <span className="text-xs text-gray-500">{ar.sublabel}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Buttons */}
            <div className="space-y-2">
              <Button
                onClick={handleGenerate}
                disabled={!hasEnoughCredits || isTaskRunning}
                className="w-full"
              >
                {isTaskRunning ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Film className="w-4 h-4 mr-2" />
                )}
                Generate {count} ({creditsNeeded} Credit{creditsNeeded !== 1 ? 's' : ''})
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleGenerateInBackground}
                disabled={!hasEnoughCredits || isTaskRunning}
                className="w-full"
              >
                <Film className="w-4 h-4 mr-2" />
                Generate in Background
              </Button>

              {isTaskRunning && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSendCurrentBatchToBackground}
                  className="w-full"
                >
                  Send Current Batch to Background
                </Button>
              )}
            </div>

            <p className="mt-3 text-xs text-gray-500">
              Background runs keep polling while you continue editing this page.
            </p>

            {!hasEnoughCredits && (
              <p className="text-red-500 text-sm text-center">
                Not enough credits. You have {subscription?.credits_available || 0} credits.
              </p>
            )}
          </div>
        </div>

        {/* Right: Results */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Generated Videos</h2>

            {backgroundBatches.length > 0 && (
              <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Background batches</p>
                    <p className="text-xs text-slate-500">
                      These keep polling while you continue generating here.
                    </p>
                  </div>
                  <span className="rounded-full bg-white px-2 py-1 text-xs font-medium text-slate-600">
                    {backgroundBatches.length} active
                  </span>
                </div>
                <div className="mt-3 space-y-2">
                  {backgroundBatches.map((batch) => (
                    <div
                      key={batch.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-800">
                          {batch.totalCount > 1 ? `${batch.totalCount} videos batch` : 'Single video task'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {batch.hasPendingPlaceholder
                            ? 'Starting request...'
                            : batch.processingCount > 0
                              ? `${batch.completedCount}/${batch.totalCount} ready, ${batch.processingCount} running`
                              : batch.failedCount > 0
                                ? `${batch.failedCount} failed, ${batch.completedCount} ready`
                                : `${batch.completedCount}/${batch.totalCount} ready`}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleBringBatchToFront(batch.id)}
                      >
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {currentBatchTasks.length > 1 && (
              <div className="mb-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Switch Result</p>
                    <p className="text-xs text-gray-500">
                      {completedTaskCount} / {totalTaskCount} ready
                      {failedTaskCount > 0 && `, ${failedTaskCount} failed`}
                    </p>
                  </div>
                  {processingTaskCount > 0 && (
                    <p className="text-xs text-amber-600">
                      {processingTaskCount} generating
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {currentBatchTasks.map((task, index) => {
                    const isActive = task.id === activeBatchTask?.id
                    const slotLabel = getTaskSlotLabel(task)
                    return (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => setActiveBatchTaskId(task.id)}
                        className={`rounded-lg border px-3 py-3 text-left transition-colors ${getTaskStatusClasses(task, isActive)}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">Video {index + 1}</span>
                          <span className="text-[11px] font-medium">
                            {getTaskStatusLabel(task)}
                          </span>
                        </div>
                        <p className="mt-1 line-clamp-2 min-h-8 text-xs leading-4 opacity-80">
                          {slotLabel || 'Video result'}
                        </p>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {generatedVideo ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Video {activeVideoNumber}
                    </p>
                    {generatedVideo.ideaTitle && (
                      <p className="text-xs text-gray-500 truncate">
                        {generatedVideo.ideaTitle}
                      </p>
                    )}
                  </div>
                  {processingTaskCount > 0 && (
                    <p className="text-xs text-gray-500">
                      {processingTaskCount} more in progress
                    </p>
                  )}
                </div>
                {currentBatchTasks.length > 1 && (
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setActiveBatchTaskId(getNextTaskId(currentBatchTasks, activeBatchTask?.id || null, 'prev'))}
                      disabled={activeVideoNumber <= 1}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <p className="text-xs text-gray-500">
                      {activeVideoNumber} / {totalTaskCount}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setActiveBatchTaskId(getNextTaskId(currentBatchTasks, activeBatchTask?.id || null, 'next'))}
                      disabled={activeVideoNumber >= currentBatchTasks.length}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
                <div className="flex justify-center">
                  <div
                    className="relative w-full overflow-hidden rounded-lg bg-black"
                    style={activeVideoFrameStyle}
                  >
                    <video
                      src={generatedVideo.url}
                      poster={generatedVideo.thumbnail}
                      controls
                      className="w-full h-full"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      {generatedVideo.duration} seconds
                    </div>
                    {generatedVideo.ideaTitle && (
                      <p className="text-xs text-gray-600">
                        Based on: {generatedVideo.ideaTitle}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled
                      title="Videos are automatically saved to your library"
                    >
                      <Star className="w-4 h-4 mr-1" />
                      Auto-saved
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleGeneratedVideoDownload}
                      disabled={!generatedVideo.url}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
                {failedTaskCount > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                    {failedTaskCount} video{failedTaskCount !== 1 ? 's' : ''} failed in this batch. You can switch above to inspect each slot or rerun the batch.
                  </div>
                )}
                {generatedVideo.firstFrameUrl && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">First Frame</h4>
                    <div className="rounded-lg overflow-hidden border border-gray-200 inline-block max-w-[280px]">
                      <img
                        src={generatedVideo.firstFrameUrl}
                        alt="First frame"
                        className="w-full h-auto max-h-[280px] object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : isTaskRunning ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                <div className="text-center">
                  <p className="text-gray-700 font-medium">
                    Generating your {totalTaskCount > 1 ? 'videos' : 'video'}...
                  </p>
                  <p className="text-sm text-gray-500">
                    {completedTaskCount > 0
                      ? `${completedTaskCount} ready, ${processingTaskCount} still running`
                      : 'This may take a few minutes'}
                  </p>
                </div>
                <div className="w-full max-w-xs bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div className="bg-primary h-full rounded-full animate-pulse" style={{ width: '60%' }} />
                </div>
              </div>
            ) : activeBatchTask?.status === 'failed' ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <AlertCircle className="w-10 h-10 text-red-500" />
                <div className="text-center">
                  <p className="text-red-700 font-medium mb-2">
                    {currentBatchTasks.length > 1 ? `Video ${activeVideoNumber} Failed` : 'Generation Failed'}
                  </p>
                  <p className="text-sm text-red-600">{taskError || 'An error occurred during video generation'}</p>
                  <Button
                    onClick={handleGenerate}
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    disabled={!hasEnoughCredits}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Film className="w-12 h-12 mb-3" />
                <p className="text-sm">Your generated videos will appear here</p>
              </div>
            )}
          </div>

          {/* Video Generation Info */}
          <div className="mt-6 bg-blue-50 rounded-xl p-4">
            <h3 className="font-medium text-blue-900 mb-2">About Video Generation</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>- Video generation typically takes 1-2 minutes</li>
              <li>- Results are automatically saved to your asset library</li>
              {referenceAssets.length > 0 && (
                <li>- Product reference images improve first-frame product consistency, angles, and detail fidelity</li>
              )}
              {useIdeas && ideaSlotCount > 0 && (
                <li>- Ideas provide creative direction; creative refs add style inspiration</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Asset Picker Modal */}
      <AssetPickerModal
        open={showAssetPicker}
        onClose={() => setShowAssetPicker(false)}
        onSelect={(asset) => appendManualReferenceAssets([asset])}
        onSelectMany={appendManualReferenceAssets}
        title="Add Product Reference Images"
        filterProductId={productId}
        assetType="image"
        requirementId={embedRequirementId ?? undefined}
      />

      {/* Import Idea Modal */}
      <ImportIdeaModal
        open={showImportModal}
        onClose={() => {
          setShowImportModal(false)
          setReplacingSlotIndex(null)
        }}
        defaultProductId={productId}
        defaultTag={activeIdeaTag}
        onConfirm={(ideas) => {
          if (ideas.length === 0) return
          setSlots((prev) => assignImportedIdeasToSlots(prev, ideas, replacingSlotIndex))
          setReplacingSlotIndex(null)
        }}
        maxSelection={maxImportSelection}
        excludeIds={importExcludedIdeaIds}
        preferredAdFormat="video"
      />
    </div>
  )
}
